import React from 'react';
import { Modal, Row, Col, Table } from 'antd';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import {
  GiftcardRedemption,
  GiftcardReport,
} from 'utils/apiClient/services/reports/types';

type GiftcardReportDetailsProps = {
  report: GiftcardReport | null;
  onClose: () => void;
  currency: string;
};

type RenderInfo = {
  label: string;
  content: React.ReactNode;
};

const GiftcardReportDetails: React.FC<GiftcardReportDetailsProps> = ({
  report,
  onClose,
  currency,
}) => {
  const intl = useIntl();

  const renderInfo = ({ label, content }: RenderInfo) => (
    <div
      css={`
        display: flex;
        justify-content: space-between;
        padding: 3px 0;
      `}
    >
      <div
        css={`
          color: rgba(0, 0, 0, 0.45);
        `}
      >
        {label}
      </div>
      <div
        css={`
          color: rgba(0, 0, 0, 0.85);
        `}
      >
        {content}
      </div>
    </div>
  );

  const renderRedemptions = (redemptions: Array<GiftcardRedemption>) => (
    <Row>
      <Col span={24}>
        <Table<GiftcardRedemption>
          css={`
            padding-bottom: 5px;
            margin-top: 5px;
          `}
          dataSource={redemptions}
          rowKey={({ order_id }) => order_id}
          size="middle"
          pagination={false}
        >
          <Table.Column<GiftcardRedemption>
            key="invoice_number"
            width="35%"
            title={<FormattedMessage defaultMessage="Name" />}
            dataIndex="invoice_number"
          />
          <Table.Column<GiftcardRedemption>
            key="completed_at"
            width="35%"
            title={<FormattedMessage defaultMessage="Date" />}
            dataIndex="completed_at"
            render={(completed_at: string) =>
              dayjs(completed_at).format('MM/DD/YYYY')
            }
          />
          <Table.Column<GiftcardRedemption>
            key="redeemed"
            width="30%"
            title={<FormattedMessage defaultMessage="Amount" />}
            dataIndex="redeemed"
            render={(redeemed: number) => (
              <FormattedNumber
                style="currency" // eslint-disable-line react/style-prop-object
                currency={currency}
                value={redeemed}
              />
            )}
          />
        </Table>
      </Col>
    </Row>
  );

  return (
    report && (
      <Modal
        width="470px"
        title={intl.formatMessage(
          {
            defaultMessage: 'Details for Coupon {redeem_code}',
          },
          { redeem_code: report.redeem_code }
        )}
        css={`
          .ant-modal-body {
            padding: 24px;
          }
          .ant-modal-footer {
            display: none;
          }
        `}
        open
        closable
        destroyOnClose
        onCancel={onClose}
      >
        {renderInfo({
          label: intl.formatMessage({
            defaultMessage: 'Coupon name',
          }),
          content: report.name || '-',
        })}
        {renderInfo({
          label: intl.formatMessage({
            defaultMessage: 'Date sold',
          }),
          content: `${dayjs(report.created_at).format('MM/DD/YYYY')} ${
            report.invoice_number ? `(${report.invoice_number})` : ''
          }`,
        })}
        {renderInfo({
          label: intl.formatMessage({
            defaultMessage: 'Original value',
          }),
          content: (
            <span css="font-weight: 800">
              <FormattedNumber
                style="currency" // eslint-disable-line react/style-prop-object
                currency={currency}
                value={report.amount}
              />
            </span>
          ),
        })}
        {renderInfo({
          label: intl.formatMessage({
            defaultMessage: 'Remaining value',
          }),
          content: (
            <span css="font-weight: 800">
              <FormattedNumber
                style="currency" // eslint-disable-line react/style-prop-object
                currency={currency}
                value={report.current_value}
              />
            </span>
          ),
        })}
        <div css="margin: 0.8em;" />
        {renderInfo({
          label: intl.formatMessage({
            defaultMessage: 'Redemptions',
          }),
          content: null,
        })}
        {renderRedemptions(report.redemption)}
      </Modal>
    )
  );
};

export default GiftcardReportDetails;
